import React, { useState, useEffect, useLayoutEffect, useCallback, useRef } from "react";
import { Alert, Button as MuiButton, List, ListItemButton, Skeleton } from "@mui/material";
import { toast } from "react-toastify";
import { useVerticalBrowseStore } from "../../stores/verticalBrowseStore";
import { listVerticalsForBrowse } from "../../services/verticalBrowseService";
import { handleError } from "../../utils/utilFunctions";
import CategoryIcon from "@mui/icons-material/Category";
import { nodeStyle } from "../TreeView/styled";
import { buttonStyle } from "./styled";
import Button from "../Button";

const VerticalsContent = ({
  verticalManagementCapability,
  onNavigate,
}: {
  verticalManagementCapability: boolean;
  onNavigate?: (path: string) => void;
}) => {
  const mode = useVerticalBrowseStore((state: any) => state.mode);
  const taxonomyStatus = useVerticalBrowseStore((state: any) => state.taxonomyStatus);
  const taxonomyError = useVerticalBrowseStore((state: any) => state.taxonomyError);
  const verticals = useVerticalBrowseStore((state: any) => state.verticals);
  const selectedVerticalId = useVerticalBrowseStore((state: any) => state.selectedVerticalId);
  const setSelectedVertical = useVerticalBrowseStore((state: any) => state.setSelectedVertical);
  const setTaxonomyStatus = useVerticalBrowseStore((state: any) => state.setTaxonomyStatus);
  const setTaxonomyError = useVerticalBrowseStore((state: any) => state.setTaxonomyError);
  const setVerticals = useVerticalBrowseStore((state: any) => state.setVerticals);
  const setBrowseAvailable = useVerticalBrowseStore((state: any) => state.setBrowseAvailable);

  const shouldFocusAfterLoad = useRef(false);
  const previousSelectedVerticalId = useRef<string | null>(null);
  const pendingFocusRestoration = useRef(false);
  const listRef = useRef<HTMLUListElement>(null);
  const retryButtonRef = useRef<HTMLButtonElement | null>(null);
  const pendingRetryFocus = useRef(false);

  const [tabStopIndex, setTabStopIndex] = useState(0);

  const loadTaxonomy = useCallback(async () => {
    setTaxonomyStatus("loading");
    setTaxonomyError(null);
    try {
      const result = await listVerticalsForBrowse();
      if (result.status === 200) {
        if (result.data.available === false) {
          setVerticals([]);
          setBrowseAvailable(false);
          return;
        }
        setVerticals(result.data.verticals);
        setTaxonomyStatus("loaded");
      } else if (result.status === 401) {
        handleError({
          status: 401,
          toast,
          errorMessage: "",
          onClose: () => {},
        });
        setBrowseAvailable(false);
      } else if (result.status === 403) {
        setBrowseAvailable(false);
        handleError({
          status: 403,
          toast,
          errorMessage: "",
          onClose: () => {},
        });
      } else {
        setTaxonomyError("Failed to load verticals.");
        setTaxonomyStatus("error");
      }
    } catch (err) {
      setTaxonomyError("Failed to load verticals.");
      setTaxonomyStatus("error");
    }
  }, [setTaxonomyStatus, setTaxonomyError, setVerticals, setBrowseAvailable]);

  useEffect(() => {
    if (mode !== "verticals") return;
    shouldFocusAfterLoad.current = true;
    loadTaxonomy();
  }, [mode, loadTaxonomy]);

  useLayoutEffect(() => {
    const shouldRestoreFocus = shouldFocusAfterLoad.current || pendingFocusRestoration.current;
    if (taxonomyStatus !== "loaded" || !shouldRestoreFocus || verticals.length === 0) return;
    shouldFocusAfterLoad.current = false;
    pendingFocusRestoration.current = false;
    const list = listRef.current;
    if (!list) return;
    const items = list.querySelectorAll<HTMLElement>('[role="option"]');
    if (items.length === 0) return;
    if (selectedVerticalId) {
      const idx = verticals.findIndex((v: { id: string; name: string }) => v.id === selectedVerticalId);
      if (idx !== -1 && idx < items.length) {
        items[idx].focus();
        setTabStopIndex(idx);
        return;
      }
    }
    items[0].focus();
    setTabStopIndex(0);
  }, [taxonomyStatus, verticals, selectedVerticalId]);

  useEffect(() => {
    const previousSelectedId = previousSelectedVerticalId.current;
    previousSelectedVerticalId.current = selectedVerticalId;

    // If the selected vertical was cleared while in verticals mode, arm focus restoration.
    // This must happen regardless of taxonomyStatus so that 404-driven loading transitions
    // (clear selection -> set loading -> await refreshed list -> loaded) preserve the intent.
    if (
      mode === "verticals" &&
      previousSelectedId &&
      !selectedVerticalId
    ) {
      pendingFocusRestoration.current = true;
      shouldFocusAfterLoad.current = true;
    }
  }, [mode, selectedVerticalId]);

  useEffect(() => {
    if (taxonomyStatus === "error" && pendingRetryFocus.current && retryButtonRef.current) {
      retryButtonRef.current.focus();
      pendingRetryFocus.current = false;
    }
  }, [taxonomyStatus]);

  const handleListKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLUListElement>) => {
      if (mode !== "verticals" || verticals.length === 0) return;
      const list = e.currentTarget;
      const items = Array.from(list.querySelectorAll<HTMLElement>('[role="option"]'));
      const target = e.target as HTMLElement;
      const currentIndex = items.indexOf(target);
      if (currentIndex === -1) return;

      let nextIndex = -1;
      switch (e.key) {
        case "ArrowDown":
          nextIndex = currentIndex < items.length - 1 ? currentIndex + 1 : 0;
          break;
        case "ArrowUp":
          nextIndex = currentIndex > 0 ? currentIndex - 1 : items.length - 1;
          break;
        case "Home":
          nextIndex = 0;
          break;
        case "End":
          nextIndex = items.length - 1;
          break;
        case "Enter":
        case " ":
          e.preventDefault();
          setSelectedVertical(verticals[currentIndex].id, verticals[currentIndex].name);
          onNavigate?.(`/main/verticals/${verticals[currentIndex].id}`);
          return;
        default:
          return;
      }

      e.preventDefault();
      items[nextIndex].focus();
      setTabStopIndex(nextIndex);
    },
    [mode, onNavigate, verticals, setSelectedVertical],
  );

  if (taxonomyStatus === "loading") {
    return (
      <div aria-busy="true" aria-live="polite">
        <p role="status">Loading verticals...</p>
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} sx={{ mb: 1 }} />
        <Skeleton variant="rectangular" height={40} />
      </div>
    );
  }

  if (taxonomyStatus === "error") {
    return (
      <Alert
        severity="error"
        role="alert"
        action={
          <MuiButton
            color="inherit"
            size="small"
            ref={retryButtonRef}
            onClick={() => {
              shouldFocusAfterLoad.current = false;
              if (retryButtonRef.current?.contains(document.activeElement)) {
                pendingRetryFocus.current = true;
              }
              loadTaxonomy();
            }}
          >
            Retry
          </MuiButton>
        }
      >
        {taxonomyError || "Failed to load verticals."}
      </Alert>
    );
  }

  if (verticals.length === 0) {
    return (
      <div>
        {verticalManagementCapability && (
          <Button
            variant="variant"
            size="large"
            color=""
            style={buttonStyle}
            onClick={() => (window.location.href = "/manage/verticals")}
          >
            <CategoryIcon />
            Manage Verticals
          </Button>
        )}
        <p>No verticals are available.</p>
      </div>
    );
  }

  return (
    <div>
      {verticalManagementCapability && (
        <Button
          variant="variant"
          size="large"
          color=""
          style={buttonStyle}
          onClick={() => (window.location.href = "/manage/verticals")}
        >
          <CategoryIcon />
          Manage Verticals
        </Button>
      )}
      <List
        ref={listRef}
        aria-label="Verticals"
        role="listbox"
        onKeyDown={handleListKeyDown}
        sx={{ py: "16px" }}
      >
        {verticals.map((v: { id: string; name: string }, idx: number) => (
          <ListItemButton
            key={v.id}
            role="option"
            aria-label={`Vertical ${v.name}`}
            aria-selected={selectedVerticalId === v.id}
            selected={selectedVerticalId === v.id}
            tabIndex={idx === tabStopIndex ? 0 : -1}
            onClick={() => {
              setSelectedVertical(v.id, v.name);
              setTabStopIndex(idx);
              onNavigate?.(`/main/verticals/${v.id}`);
            }}
            style={{
              ...nodeStyle,
              boxSizing: "border-box",
              backgroundColor: selectedVerticalId === v.id ? "#C1E7FE" : undefined,
            }}
            sx={{
              "&&.Mui-selected": { backgroundColor: "#C1E7FE" },
              "&&.Mui-selected:hover": { backgroundColor: "#C1E7FE" },
            }}
          >
            {v.name}
          </ListItemButton>
        ))}
      </List>
    </div>
  );
};

export default VerticalsContent;
